/**
 * components/ShellOverlay.tsx —— **框架级浮层**（徽章/toast/状态药丸 + 宿主注入条目）。
 *
 * 真源：`102:frontend_src/src/components/ShellOverlay.vue`（131 行）——**抄形态/尺寸/交互，译 Vue→React+TS**。
 * 依据：《UI 全局布局壳规范》v1.2 §6（层容器 pointer-events:none；条目自声明可交互区；按序互不遮挡；
 *      **不预置示例条目**）；§2 座位 `shell.overlay`（kind=list）。
 *
 * 契约（逐条承源件 U-17 注）：
 *  · 容器**层穿透**：`pointer-events: none`（本件走 inline style 以便机判；视觉等价源件样式层声明）；
 *  · 条目 **opt-in**：结构化条目默认不拦指针；`item.pointerEvents` 自声明可交互区（源件 :style 逐字）；
 *    toast 的可视体自行 opt-in `auto`（源件样式层 `.xg-shell-overlay__toast{pointer-events:auto}` 同语义，
 *    本件并入条目 inline——opt-in 粒度＝**条目**）；
 *  · **按注册序排列互不遮挡**（列布局＋gap，条目各占一行 ⇒ DOM 序＝注册序）；
 *  · 宿主注入条目＝`children`（源件默认 slot；如设置面板）——容器一律不拦指针，条目自行 opt-in；
 *  · live 播报只由条目自身 `role="status"` 承担 ⇒ **根上不挂 aria-live**（源件逐字：否则宿主条目内容被卷入播报）；
 *  · **不预置示例条目**：items/children 皆空 ⇒ 空容器（声明面 `overlay.preset_entries: []`）。
 *
 * 纪律：本件不解释条目业务语义（谁投递谁定文案）；色/字号/间距一律 `var(--xg-*)`；零取数。
 */
import React from "react";

/** 浮层条目（§3.7 清单三类 + 自定义；宿主按注册序投递） */
export interface ShellOverlayItem {
  id: string;
  type?: "toast" | "badge" | "pill" | "custom";
  /** toast：文案 */
  message?: string;
  /** toast：档位（success/warning/danger；色＋文本等价由文案承担） */
  severity?: "success" | "warning" | "danger";
  /** badge：计数（≤99 显示原值，>99 显示 99+——§1ter·四 徽标规格） */
  count?: number;
  /** badge：读屏标注 */
  ariaLabel?: string;
  /** pill：文案 */
  label?: string;
  /** opt-in 可交互区（"auto" ⇒ 该条目接指针；缺省不拦，§6） */
  pointerEvents?: string;
  /** custom：宿主注入的节点（承源件具名 slot `item` 的 scoped 用法） */
  node?: React.ReactNode;
}

export interface ShellOverlayProps {
  /** 浮层条目列表（只读，按注册序） */
  items?: ShellOverlayItem[];
  /** 宿主注入的层条目（源件默认 slot） */
  children?: React.ReactNode;
}

/** 徽标计数呈现（§1ter·四：**≤99 显示原值，>99 显示 99+**） */
export function badgeCountText(count: number | undefined): string {
  if (count == null || !Number.isFinite(count)) return "";
  return count > 99 ? "99+" : String(count);
}

/** 框架级浮层容器 */
export function ShellOverlay(props: ShellOverlayProps): React.ReactElement {
  const items = props.items ?? [];
  return (
    <div
      className="xg-shell-overlay"
      data-testid="shell-overlay"
      style={{ pointerEvents: "none" }} // §6 层容器穿透（机判面；样式层兜底见 STYLE）
    >
      {items.map((item) => (
        <div
          key={item.id}
          className={"xg-shell-overlay__item" + (item.type ? ` xg-shell-overlay__item--${item.type}` : "")}
          data-testid={"overlay-item-" + item.id}
          style={(() => {
            // 条目 opt-in：显式声明优先；toast 恒可交互（承源件样式层 `.xg-shell-overlay__toast{pointer-events:auto}`，
            // 本件 opt-in 粒度＝条目——toast 可视体是条目唯一子元素，命中区等价）
            const opt = item.pointerEvents ?? (item.type === "toast" ? "auto" : undefined);
            return opt ? { pointerEvents: opt as React.CSSProperties["pointerEvents"] } : undefined;
          })()}
          role="status"
        >
          {item.type === "toast" ? (
            <span className={"xg-shell-overlay__toast" + (item.severity ? ` xg-shell-overlay__toast--${item.severity}` : "")}>
              {item.message}
            </span>
          ) : item.type === "badge" ? (
            <span className="xg-shell-overlay__badge" aria-label={item.ariaLabel}>{badgeCountText(item.count)}</span>
          ) : item.type === "pill" ? (
            <span className="xg-shell-overlay__pill">{item.label}</span>
          ) : (
            item.node ?? null // 自定义内容（承源件 slot name=item）
          )}
        </div>
      ))}

      {/* 宿主注入的层条目（§6：容器不拦截，条目自行 opt-in 指针） */}
      {props.children}

      <style>{STYLE}</style>
    </div>
  );
}

/** 自包含样式（源件 `<style scoped>` 译入；真名 token；`xg-shell-overlay` 前缀隔离） */
import { shellOverlayStyle as STYLE } from "../styles/inlineStyles";

export default ShellOverlay;
